import {Component, Element, h, Host, Prop, State, Watch} from "@stencil/core";
import type {TrumpfNotificationActionLink, TrumpfNotificationType} from "./notification-item";

@Component({
	tag: "uxn-notification-text",
	styleUrl: "notification-text.scss",
	shadow: false,
})
export class NotificationText {
	private baseClass = "uxn-notification-text";

	/** The host html element. */
	@Element() el: HTMLUxnNotificationTextElement;

	/** The subject of the notification. */
	@Prop() readonly subject: string = "";

	/**
	 * The message of the notification.
	 * @deprecated Use the message slot instead.
	 */
	@Prop() readonly message: string = "";

	/** Update notification state*/
	@Prop() readonly notificationUpdateError: boolean = false;

	/** Update notification error text */
	@Prop() readonly notificationUpdateErrorText: string = "Update failed. Please try again.";

	/** Link of the notification, e.g. link for read more button */
	@Prop() readonly link: TrumpfNotificationActionLink["link"] = "";

	/** Link text of the notification, e.g. read more */
	@Prop() readonly linkText: TrumpfNotificationActionLink["linkText"] = "";

	/** Type of notification. Can be 'Overview' or 'Detail'. */
	@Prop() readonly type: TrumpfNotificationType = "Detail";

	/** Is the notification text collapsed. */
	@State() collapsed = true;

	/** Is the notification text too long. */
	@State() tooLong = false;

	#setTooLong = () => {
		const wrapperDiv = this.el.querySelector(`div.${this.baseClass}__wrapper`);
		this.tooLong = wrapperDiv.scrollHeight > Math.round(wrapperDiv.getBoundingClientRect().height);
	};

	@Watch("subject")
	@Watch("message")
	onContentChange() {
		this.#setTooLong();
	}

	componentDidLoad() {
		this.#setTooLong();
	}

	render() {
		const {baseClass, subject, message, link, linkText, type, notificationUpdateError, notificationUpdateErrorText} = this;

		const toggleCollapsed = (e: MouseEvent) => {
			e.preventDefault();
			this.collapsed = !this.collapsed;
		};

		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<Host>
				<div class={baseClass}>
					<div
						class={{
							[`${baseClass}__wrapper`]: true,
							[`${baseClass}__wrapper_gradient`]: this.tooLong && this.collapsed,
							[`${baseClass}__wrapper_expanded`]: !this.collapsed,
						}}
					>
						{notificationUpdateError && <div class={`${baseClass}__error ux-text-normal`}>{notificationUpdateErrorText}</div>}
						{type.toLowerCase() === "overview" && <div class={`${baseClass}__subject ux-text-normal ux-text-normal--bold`}>{subject}</div>}
						{type.toLowerCase() === "detail" && <div class={`${baseClass}__subject ux-text-title ux-text-title--bold`}>{subject}</div>}
						{type.toLowerCase() === "overview" && <div class={`${baseClass}__message ux-text-normal`}>{message ? message : <slot name="message" />}</div>}
						{type.toLowerCase() === "detail" && (
							<div class={`${baseClass}__message ux-text-normal`}>
								{message ? message : <slot name="message" />}
								{link && linkText && (
									<a href={link} target="_self" class="ux-text-link ux-text-link--bold ux-text-link--green">
										{linkText}
										<ux-icon name="arrow-right" weight="normal" />
									</a>
								)}
							</div>
						)}
					</div>
					{this.tooLong && (
						<button class="ux-icon-button" onClick={(e) => toggleCollapsed(e)}>
							<ux-icon class={this.collapsed ? "" : `${baseClass}__icon`} size-s="15" name="chevron-down" />
						</button>
					)}
				</div>
			</Host>
		);
	}
}
